import React, { useState, useEffect } from 'react';
import { 
  Mountain, LogOut, RefreshCw, ShoppingCart, DollarSign, Clock, 
  CheckCircle, FileText, X, Settings, Image, Sliders, Upload, Save,
  Plus, Trash2, Edit, Package, Truck
} from 'lucide-react';
import { API_URL, resolveImageUrl } from '../config';
import './AdminDashboard.css';



const defaultHeroSlider = [
  {
    id: 1,
    title: "Pure Himalayan Makhana",
    subtitle: "DIRECT FROM THE SOURCE",
    description: "Superfood harvested directly from pristine Himalayan water bodies. Loaded with protein, calcium, and essential nutrients.",
    offer: "15%",
    productImage: "https://images.unsplash.com/photo-1596547609652-9cf5d8d76921?auto=format&fit=crop&q=80&w=600",
    bgImage: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=1600"
  },
  {
    id: 2,
    title: "California Almonds (Bold)",
    subtitle: "HANDPICKED & SORTED",
    description: "Extra-large, crunchy almonds rich in Vitamin E, dietary fiber, and healthy fats. Your perfect daily morning snack.",
    offer: "20%",
    productImage: "https://images.unsplash.com/photo-1508061253366-f7da158b6d46?auto=format&fit=crop&q=80&w=600",
    bgImage: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&q=80&w=1600"
  },
  {
    id: 3,
    title: "Snow-White Kashmiri Walnuts",
    subtitle: "ORGANIC BRAIN FOOD",
    description: "Premium quality paper-shell walnuts from the valleys of Kashmir. Rich in Omega-3 fatty acids and antioxidants.",
    offer: "10%",
    productImage: "https://images.unsplash.com/photo-1601004890684-d8cbf643f5f2?auto=format&fit=crop&q=80&w=600",
    bgImage: "https://images.unsplash.com/photo-1454496522488-7a8e488e8606?auto=format&fit=crop&q=80&w=1600"
  },
  {
    id: 4,
    title: "Premium Whole Cashews",
    subtitle: "DELICIOUSLY CREAMY",
    description: "Crispy, naturally sweet whole cashew kernels. Hygienically packed to retain their natural oils and fresh taste.",
    offer: "25%",
    productImage: "https://images.unsplash.com/photo-1600002415506-dd0609260c18?auto=format&fit=crop&q=80&w=600",
    bgImage: "https://images.unsplash.com/photo-1486873249359-2731bd6dafc7?auto=format&fit=crop&q=80&w=1600"
  }
];

const defaultFeatures = {
  title: "Why Choose Himalayan Nuts?",
  description: "Every single bite is packed with pure, raw power sourced from nature's lap.",
  image: "/images/makhana_bowl.jpg",
  benefits: [
    {
      title: "High in Protein",
      description: "Helps build muscle, repair tissues, and provides long-lasting natural energy.",
      icon: "Zap"
    },
    {
      title: "Rich in Nutrients",
      description: "Packed with essential vitamins, minerals, and antioxidants for overall body health.",
      icon: "Leaf"
    },
    {
      title: "Heart Friendly",
      description: "Contains zero trans fats and low sodium to keep cholesterol and blood pressure stable.",
      icon: "Heart"
    },
    {
      title: "Immunity Booster",
      description: "Loaded with zinc and magnesium to naturally enhance your body's immune defense.",
      icon: "Shield"
    },
    {
      title: "Healthy Snacking",
      description: "A low-calorie, high-fiber, gluten-free option that satisfies hunger wholesomely.",
      icon: "Smile"
    }
  ]
};

const defaultFooter = {
  logoTitle: "Himalayannuts",
  description: "Sourcing the finest, premium quality nuts, dry fruits, and lotus seeds directly from the foothills of the Himalayas. Pure, natural, and wholesome.",
  instagramUrl: "#instagram",
  facebookUrl: "#facebook",
  youtubeUrl: "#youtube"
};

const defaultBottomFeatures = [
  {
    icon: "Lock",
    title: "Secure Payments",
    subtitle: "100% Protected Transactions"
  },
  {
    icon: "RotateCcw",
    title: "Easy Returns",
    subtitle: "7-Day Hassle-Free Returns"
  },
  {
    icon: "Truck",
    title: "Fast Delivery",
    subtitle: "Direct to Your Doorstep"
  },
  {
    icon: "Headphones",
    title: "Customer Support",
    subtitle: "24/7 Dedicated Assistance"
  }
];

const defaultCategories = [
  {
    id: 1,
    name: 'Almonds',
    hindiName: 'Badam',
    image: 'https://images.unsplash.com/photo-1508061253366-f7da158b6d46?auto=format&fit=crop&q=80&w=300'
  },
  {
    id: 2,
    name: 'Cashews',
    hindiName: 'Kaju',
    image: 'https://images.unsplash.com/photo-1600002415506-dd0609260c18?auto=format&fit=crop&q=80&w=300'
  },
  {
    id: 3,
    name: 'Walnuts',
    hindiName: 'Akhrot',
    image: 'https://images.unsplash.com/photo-1601004890684-d8cbf643f5f2?auto=format&fit=crop&q=80&w=300'
  },
  {
    id: 4,
    name: 'Pistachios',
    hindiName: 'Pista',
    image: 'https://images.unsplash.com/photo-1543362906-acfc16c67564?auto=format&fit=crop&q=80&w=300'
  },
  {
    id: 5,
    name: 'Makhana',
    hindiName: 'Makhana',
    image: 'https://images.unsplash.com/photo-1596547609652-9cf5d8d76921?auto=format&fit=crop&q=80&w=300'
  },
  {
    id: 6,
    name: 'Raisins',
    hindiName: 'Kishmish',
    image: 'https://images.unsplash.com/photo-1568254183919-78a4f43a2877?auto=format&fit=crop&q=80&w=300'
  },
  {
    id: 7,
    name: 'Seeds & Others',
    hindiName: 'Beej',
    image: 'https://images.unsplash.com/photo-1596701062351-8c2c14d1fdd0?auto=format&fit=crop&q=80&w=300'
  },
  {
    id: 8,
    name: 'Combo Packs',
    hindiName: 'Combo',
    image: 'https://images.unsplash.com/photo-1608686207856-001b95cf60ca?auto=format&fit=crop&q=80&w=300'
  }
];

const defaultPaymentSettings = {
  upiId: 'himalayannuts@upi',
  qrCode: ''
};



const tabButtonStyle = (isActive) => ({
  padding: '8px 16px',
  borderRadius: '20px',
  border: isActive ? '1px solid var(--primary-green)' : '1px solid var(--border-color)',
  backgroundColor: isActive ? 'var(--primary-green)' : '#ffffff',
  color: isActive ? '#ffffff' : 'var(--dark-text)',
  fontWeight: 700,
  fontSize: '12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  boxShadow: isActive ? '0 4px 10px rgba(46, 93, 52, 0.15)' : 'none'
});

const AdminDashboard = ({ handleLogout }) => {
  // Main Tab Navigation: 'orders' or 'settings'
  const [activeMainTab, setActiveMainTab] = useState('orders');
  
  // Settings Category Navigation: 'hero' | 'features' | 'footer'
  const [settingsSubTab, setSettingsSubTab] = useState('hero');

  // Orders State
  const [orders, setOrders] = useState([]);
  const [ordersFilterTab, setOrdersFilterTab] = useState('pending');
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);

  // Settings State
  const [heroSlides, setHeroSlides] = useState(defaultHeroSlider);
  const [featuresData, setFeaturesData] = useState(defaultFeatures);
  const [footerData, setFooterData] = useState(defaultFooter);
  const [bottomFeaturesData, setBottomFeaturesData] = useState(defaultBottomFeatures);
  const [categoriesData, setCategoriesData] = useState(defaultCategories);
  const [paymentSettingsData, setPaymentSettingsData] = useState(defaultPaymentSettings);
  const [brandingData, setBrandingData] = useState({
    brandName: 'Himalayannuts',
    tagline: 'Pure. Natural. Wholesome.',
    logoUrl: '/images/logo.jpg'
  });
  const [aboutUsData, setAboutUsData] = useState({
    title: '',
    subtitle: '',
    story: '',
    image: '',
    values: [
      { title: '', description: '' },
      { title: '', description: '' },
      { title: '', description: '' }
    ]
  });
  const [blogPostsData, setBlogPostsData] = useState([]);
  const [editingBlogPost, setEditingBlogPost] = useState(null);
  const [isAddingBlogPost, setIsAddingBlogPost] = useState(false);
  const [blogPostForm, setBlogPostForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    image: '',
    date: '',
    author: ''
  });
  const [contactSettingsData, setContactSettingsData] = useState({
    email: '',
    phone: '',
    address: '',
    whatsapp: '',
    instagram: '',
    facebook: ''
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [uploadingField, setUploadingField] = useState(null); // identifier of field being uploaded

  // Products State
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const initialProductForm = {
    title: '',
    price: '',
    oldPrice: '',
    image: '',
    badge: '',
    rating: '5.0',
    reviewsCount: '0',
    type: 'nuts'
  };
  const [productForm, setProductForm] = useState(initialProductForm);

  const filteredOrders = orders.filter(order => {
    if (ordersFilterTab === 'pending') {
      return order.status === 'PENDING';
    }
    if (ordersFilterTab === 'payment_verified') {
      return order.status === 'PAYMENT_VERIFIED';
    }
    if (ordersFilterTab === 'preparing_packed') {
      return order.status === 'PREPARING' || order.status === 'PACKED';
    }
    if (ordersFilterTab === 'out_for_delivery') {
      return order.status === 'OUT_FOR_DELIVERY';
    }
    if (ordersFilterTab === 'delivered') {
      return order.status === 'DELIVERED';
    }
    return true;
  });

  useEffect(() => {
    fetchOrders();
    fetchSettings();
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrders = async () => {
    setLoadingOrders(true);
    setFetchError('');
    const token = localStorage.getItem('admin_token');
    
    if (!token) {
      handleLogout();
      return;
    }

    try {
      const response = await fetch(`${API_URL}/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.status === 401 || response.status === 403) {
        throw new Error('Your session has expired. Please log in again.');
      }

      if (!response.ok) {
        throw new Error('Failed to retrieve orders from backend.');
      }
      
      const data = await response.json();
      setOrders(data);
    } catch (err) {
      console.error(err);
      setFetchError(err.message || 'Could not connect to the backend server.');
      if (err.message.includes('expired') || err.message.includes('log in again')) {
        handleLogout();
      }
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await fetch(`${API_URL}/products`);
      if (!response.ok) {
        throw new Error('Failed to load products.');
      }
      const data = await response.json();
      setProducts(data);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');
    if (!token) {
      handleLogout();
      return;
    }

    if (!productForm.title || !productForm.price || !productForm.image || !productForm.type) {
      alert('Please fill in all required product fields.');
      return;
    }

    const payload = {
      title: productForm.title,
      price: parseFloat(productForm.price),
      oldPrice: productForm.oldPrice ? parseFloat(productForm.oldPrice) : 0.0,
      image: productForm.image,
      badge: productForm.badge || null,
      rating: parseFloat(productForm.rating),
      reviewsCount: parseInt(productForm.reviewsCount),
      type: productForm.type
    };

    const isEdit = !!editingProduct;
    const url = isEdit ? `${API_URL}/products/${editingProduct.id}` : `${API_URL}/products`;
    const method = isEdit ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to save product.');
      }

      alert(`Product ${isEdit ? 'updated' : 'created'} successfully!`);
      setEditingProduct(null);
      setIsAddingProduct(false);
      setProductForm(initialProductForm);
      fetchProducts();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error occurred while saving product.');
    }
  };

  const handleProductDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    const token = localStorage.getItem('admin_token');
    if (!token) {
      handleLogout();
      return;
    }

    try {
      const res = await fetch(`${API_URL}/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to delete product.');
      }

      alert('Product deleted successfully!');
      fetchProducts();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error occurred while deleting product.');
    }
  };

  const fetchSettings = async () => {
    setLoadingSettings(true);
    try {
      const [heroRes, featuresRes, footerRes, bottomRes, categoriesRes, paymentRes, brandingRes, aboutUsRes, blogRes, contactRes] = await Promise.all([
        fetch(`${API_URL}/settings/hero_slider`),
        fetch(`${API_URL}/settings/features_section`),
        fetch(`${API_URL}/settings/footer`),
        fetch(`${API_URL}/settings/bottom_features`),
        fetch(`${API_URL}/settings/categories`),
        fetch(`${API_URL}/settings/payment_settings`),
        fetch(`${API_URL}/settings/branding`),
        fetch(`${API_URL}/settings/about_us`),
        fetch(`${API_URL}/settings/blog_posts`),
        fetch(`${API_URL}/settings/contact_settings`)
      ]);
      
      if (heroRes.ok) {
        const data = await heroRes.json();
        if (Array.isArray(data) && data.length > 0) setHeroSlides(data);
      }
      if (featuresRes.ok) {
        const data = await featuresRes.json();
        if (data && Array.isArray(data.benefits)) setFeaturesData(data);
      }
      if (footerRes.ok) {
        const data = await footerRes.json();
        if (data && data.logoTitle !== undefined) setFooterData(data);
      }
      if (bottomRes.ok) {
        const data = await bottomRes.json();
        if (Array.isArray(data) && data.length > 0) setBottomFeaturesData(data);
      }
      if (categoriesRes && categoriesRes.ok) {
        const data = await categoriesRes.json();
        if (Array.isArray(data) && data.length > 0) setCategoriesData(data);
      }
      if (paymentRes && paymentRes.ok) {
        const data = await paymentRes.json();
        if (data && data.upiId !== undefined) setPaymentSettingsData(data);
      }
      if (brandingRes && brandingRes.ok) {
        const data = await brandingRes.json();
        if (data && data.brandName !== undefined) setBrandingData(data);
      }
      if (aboutUsRes && aboutUsRes.ok) {
        const data = await aboutUsRes.json();
        if (data && data.title !== undefined) setAboutUsData(data);
      }
      if (blogRes && blogRes.ok) {
        const data = await blogRes.json();
        if (Array.isArray(data)) setBlogPostsData(data);
      }
      if (contactRes && contactRes.ok) {
        const data = await contactRes.json();
        if (data && data.email !== undefined) setContactSettingsData(data);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      handleLogout();
      return;
    }

    try {
      const response = await fetch(`${API_URL}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        throw new Error('Your session has expired. Please log in again.');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update status on server.');
      }

      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );
    } catch (err) {
      console.error(err);
      alert(err.message || 'Could not update order status.');
      if (err.message.includes('expired') || err.message.includes('log in again')) {
        handleLogout();
      }
    }
  };

  const saveSetting = async (key, value) => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      handleLogout();
      return;
    }

    setSavingKey(key);
    try {
      const res = await fetch(`${API_URL}/settings/${key}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ value })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `Failed to save ${key} settings.`);
      }
      alert(`Settings for storefront "${key.replace('_', ' ')}" updated successfully!`);
      if (key === 'branding' || key === 'about_us' || key === 'blog_posts' || key === 'contact_settings') {
        window.location.reload();
      }
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error occurred while saving settings.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleFileUpload = async (file, fieldId, onUploaded) => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      handleLogout();
      return;
    }

    setUploadingField(fieldId);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const res = await fetch(`${API_URL}/settings/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to upload photo.');
      }
      
      onUploaded(data.url);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error uploading file.');
    } finally {
      setUploadingField(null);
    }
  };

  const handleSlideChange = (index, field, value) => {
    setHeroSlides(prev => prev.map((s, idx) => idx === index ? { ...s, [field]: value } : s));
  };

  const handleBenefitChange = (index, field, value) => {
    setFeaturesData(prev => {
      const newBenefits = prev.benefits.map((b, idx) => idx === index ? { ...b, [field]: value } : b);
      return { ...prev, benefits: newBenefits };
    });
  };

  const computeMetrics = () => {
    const totalOrders = orders.length;
    const totalSales = orders
      .filter((o) => o.status !== 'PENDING')
      .reduce((sum, o) => sum + o.totalAmount, 0);
    const pendingVerification = orders.filter((o) => o.status === 'PENDING').length;
    const completedDeliveries = orders.filter((o) => o.status === 'DELIVERED').length;
    const preparingCount = orders.filter((o) => o.status === 'PREPARING' || o.status === 'PACKED').length;
    const outForDeliveryCount = orders.filter((o) => o.status === 'OUT_FOR_DELIVERY').length;

    return { totalOrders, totalSales, pendingVerification, completedDeliveries, preparingCount, outForDeliveryCount };
  };

  const metrics = computeMetrics();

  return (
    <div className="admin-dashboard-container">
      {/* Admin Title Header */}
      <header className="admin-header-row">
        <div className="container admin-header-content">
          <div className="admin-header-title-box">
            <div className="login-logo-icon">
              <img 
                src={resolveImageUrl(brandingData.logoUrl)} 
                alt={`${brandingData.brandName} Logo`} 
                className="login-logo-img" 
              />
            </div>
            <span className="login-logo-title">{brandingData.brandName}</span>
            <span className="admin-badge">Admin console</span>
          </div>
          
          <button onClick={handleLogout} className="admin-logout-btn">
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </header>

      {/* Main Tabs Navigation */}
      <div className="admin-tabs-nav-bar">
        <div className="container tabs-nav-content">
          <button 
            className={`admin-nav-tab-btn ${activeMainTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('orders')}
          >
            <ShoppingCart size={16} />
            <span>Orders Management</span>
          </button>
          <button 
            className={`admin-nav-tab-btn ${activeMainTab === 'products' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('products')}
          >
            <Package size={16} />
            <span>Product Catalog</span>
          </button>
          <button 
            className={`admin-nav-tab-btn ${activeMainTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveMainTab('settings')}
          >
            <Settings size={16} />
            <span>Storefront Settings</span>
          </button>
        </div>
      </div>

      {activeMainTab === 'orders' && (
        /* ====================================
           1. ORDERS VIEW
           ==================================== */
        <main className="container admin-main-content">
          <div className="metrics-row">
            <div className="metric-card-box">
              <div className="metric-icon-box">
                <ShoppingCart size={22} />
              </div>
              <div className="metric-value-box">
                <span className="metric-card-label">Total Orders</span>
                <span className="metric-card-num">{metrics.totalOrders}</span>
              </div>
            </div>

            <div className="metric-card-box">
              <div className="metric-icon-box" style={{ backgroundColor: '#ebfbee', color: '#2b8a3e' }}>
                <DollarSign size={22} />
              </div>
              <div className="metric-value-box">
                <span className="metric-card-label">Total Revenue</span>
                <span className="metric-card-num">₹{metrics.totalSales.toLocaleString()}</span>
              </div>
            </div>

            <div className="metric-card-box">
              <div className="metric-icon-box" style={{ backgroundColor: '#fff9db', color: '#f59f00' }}>
                <Clock size={22} />
              </div>
              <div className="metric-value-box">
                <span className="metric-card-label">Pending Verification</span>
                <span className="metric-card-num">{metrics.pendingVerification}</span>
              </div>
            </div>

            <div className="metric-card-box">
              <div className="metric-icon-box" style={{ backgroundColor: '#e8f4fd', color: '#1d88e5' }}>
                <Package size={22} />
              </div>
              <div className="metric-value-box">
                <span className="metric-card-label">Preparing & Packed</span>
                <span className="metric-card-num">{metrics.preparingCount}</span>
              </div>
            </div>

            <div className="metric-card-box">
              <div className="metric-icon-box" style={{ backgroundColor: '#fff4e6', color: '#fd7e14' }}>
                <Truck size={22} />
              </div>
              <div className="metric-value-box">
                <span className="metric-card-label">Out For Delivery</span>
                <span className="metric-card-num">{metrics.outForDeliveryCount}</span>
              </div>
            </div>

            <div className="metric-card-box">
              <div className="metric-icon-box" style={{ backgroundColor: '#e6fcf5', color: '#087f5b' }}>
                <CheckCircle size={22} />
              </div>
              <div className="metric-value-box">
                <span className="metric-card-label">Completed Deliveries</span>
                <span className="metric-card-num">{metrics.completedDeliveries}</span>
              </div>
            </div>
          </div>

          <div className="orders-table-panel">
            <div className="panel-header-row">
              <h2 className="panel-title">Customer Orders Management</h2>
              <button onClick={fetchOrders} className="refresh-btn" disabled={loadingOrders}>
                <RefreshCw size={14} className={loadingOrders ? 'spin-animation' : ''} />
                <span>Refresh Orders</span>
              </button>
            </div>

            {/* SEGREGATION TABS */}
            <div className="orders-filter-tabs">
              <button 
                onClick={() => setOrdersFilterTab('pending')}
                style={tabButtonStyle(ordersFilterTab === 'pending')}
              >
                Pending ({orders.filter(o => o.status === 'PENDING').length})
              </button>
              <button 
                onClick={() => setOrdersFilterTab('payment_verified')}
                style={tabButtonStyle(ordersFilterTab === 'payment_verified')}
              >
                Payment Verified ({orders.filter(o => o.status === 'PAYMENT_VERIFIED').length})
              </button>
              <button 
                onClick={() => setOrdersFilterTab('preparing_packed')}
                style={tabButtonStyle(ordersFilterTab === 'preparing_packed')}
              >
                Preparing & Packed ({orders.filter(o => o.status === 'PREPARING' || o.status === 'PACKED').length})
              </button>
              <button 
                onClick={() => setOrdersFilterTab('out_for_delivery')}
                style={tabButtonStyle(ordersFilterTab === 'out_for_delivery')}
              >
                Out For Delivery ({orders.filter(o => o.status === 'OUT_FOR_DELIVERY').length})
              </button>
              <button 
                onClick={() => setOrdersFilterTab('delivered')}
                style={tabButtonStyle(ordersFilterTab === 'delivered')}
              >
                Delivered ({orders.filter(o => o.status === 'DELIVERED').length})
              </button>
            </div>

            {fetchError && (
              <div style={{ padding: '24px', color: '#ff4757', backgroundColor: '#fff5f5', borderBottom: '1px solid var(--border-color)', fontWeight: 500 }}>
                ⚠️ {fetchError}
              </div>
            )}

            {loadingOrders && orders.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--gray)', fontFamily: 'var(--font-nav)', fontWeight: 500 }}>
                Loading orders from database...
              </div>
            ) : filteredOrders.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--gray)', fontFamily: 'var(--font-nav)', fontWeight: 500 }}>
                No orders found under this status tab.
              </div>
            ) : (
              <div className="table-responsive-container">
                <table className="orders-data-table">
                  <thead>
                    <tr>
                      <th>Order Date</th>
                      <th>Order ID / Track ID</th>
                      <th>Customer Name</th>
                      <th>Phone & Address</th>
                      <th>Items Summary</th>
                      <th>Amount</th>
                      <th>Receipt</th>
                      <th>Status Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.map((order) => {
                      let items = [];
                      try {
                        items = typeof order.orderSummary === 'string' 
                          ? JSON.parse(order.orderSummary) 
                          : order.orderSummary;
                      } catch (e) {
                        console.error('Error parsing order summary:', e);
                      }

                      return (
                        <tr key={order.id}>
                          <td>
                            {new Date(order.createdAt).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                          <td>
                            <span className="order-id-txt">{order.id}</span>
                          </td>
                          <td>
                            <strong style={{ color: 'var(--dark-green)' }}>{order.customerName}</strong>
                            {order.message && (
                              <div style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '4px', fontStyle: 'italic' }}>
                                " {order.message} "
                              </div>
                            )}
                          </td>
                          <td>
                            <div>📞 {order.phone}</div>
                            <div style={{ fontSize: '12px', color: 'var(--gray)', marginTop: '4px', maxWidth: '200px' }}>
                              📍 {order.address}
                            </div>
                          </td>
                          <td className="order-items-cell">
                            {Array.isArray(items) ? (
                              items.map((item, idx) => (
                                <div key={idx} className="order-item-desc">
                                  • {item.title || item.name} <span style={{ fontWeight: 600 }}>x{item.qty || item.quantity}</span> (₹{item.price})
                                </div>
                              ))
                            ) : (
                              <span style={{ color: 'var(--light-gray)' }}>No items detail</span>
                            )}
                          </td>
                          <td>
                            <strong style={{ fontSize: '15px', color: 'var(--dark-green)' }}>₹{order.totalAmount}</strong>
                          </td>
                          <td>
                            {order.paymentScreenshot ? (
                              <button 
                                onClick={() => setSelectedScreenshot(resolveImageUrl(order.paymentScreenshot))}
                                className="screenshot-view-btn"
                              >
                                <FileText size={14} />
                                <span>View Receipt</span>
                              </button>
                            ) : (
                              <span style={{ color: 'var(--light-gray)', fontStyle: 'italic' }}>No Receipt</span>
                            )}
                          </td>
                          <td>
                            <select
                              className={`status-dropdown-select ${
                                order.status === 'PENDING' ? 'status-pending' :
                                order.status === 'PAYMENT_VERIFIED' ? 'status-verified' :
                                order.status === 'PREPARING' ? 'status-preparing' :
                                order.status === 'PACKED' ? 'status-packed' :
                                order.status === 'OUT_FOR_DELIVERY' ? 'status-out' :
                                'status-delivered'
                              }`}
                              value={order.status}
                              onChange={(e) => handleStatusChange(order.id, e.target.value)}
                            >
                              <option value="PENDING">PENDING</option>
                              <option value="PAYMENT_VERIFIED">PAYMENT VERIFIED</option>
                              <option value="PREPARING">PREPARING</option>
                              <option value="PACKED">PACKED</option>
                              <option value="OUT_FOR_DELIVERY">OUT FOR DELIVERY</option>
                              <option value="DELIVERED">DELIVERED</option>
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      )}

      {activeMainTab === 'settings' && (
        /* ====================================
           2. SETTINGS VIEW
           ==================================== */
        <main className="container admin-main-content">
          <div className="settings-panel-layout">
            {/* Sidebar Navigation */}
            <aside className="settings-sidebar">
              <button 
                className={`settings-sidebar-btn ${settingsSubTab === 'hero' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('hero')}
              >
                <Sliders size={16} />
                <span>Hero Banner Slider</span>
              </button>
              <button 
                className={`settings-sidebar-btn ${settingsSubTab === 'features' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('features')}
              >
                <Image size={16} />
                <span>Benefits Section</span>
              </button>
              <button 
                className={`settings-sidebar-btn ${settingsSubTab === 'footer' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('footer')}
              >
                <Mountain size={16} />
                <span>Footer Section</span>
              </button>
              <button 
                className={`settings-sidebar-btn ${settingsSubTab === 'categories' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('categories')}
              >
                <Package size={16} />
                <span>Categories Section</span>
              </button>
              <button 
                className={`settings-sidebar-btn ${settingsSubTab === 'bottom_features' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('bottom_features')}
              >
                <CheckCircle size={16} />
                <span>Bottom Features</span>
              </button>
              <button 
                className={`settings-sidebar-btn ${settingsSubTab === 'payment' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('payment')}
              >
                <DollarSign size={16} />
                <span>Payment Settings</span>
              </button>
              <button 
                className={`settings-sidebar-btn ${settingsSubTab === 'branding' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('branding')}
              >
                <Settings size={16} />
                <span>Branding Settings</span>
              </button>
              <button 
                className={`settings-sidebar-btn ${settingsSubTab === 'about_us' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('about_us')}
              >
                <FileText size={16} />
                <span>About Us Settings</span>
              </button>
              <button 
                className={`settings-sidebar-btn ${settingsSubTab === 'blog_posts' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('blog_posts')}
              >
                <FileText size={16} />
                <span>Blog Settings</span>
              </button>
              <button 
                className={`settings-sidebar-btn ${settingsSubTab === 'contact_settings' ? 'active' : ''}`}
                onClick={() => setSettingsSubTab('contact_settings')}
              >
                <Sliders size={16} />
                <span>Contact Settings</span>
              </button>
            </aside>

            {/* Editing Form Panel */}
            <section className="settings-content-pane">
              {loadingSettings ? (
                <div className="settings-loading-box">
                  <RefreshCw size={24} className="spin-animation" />
                  <span>Loading store settings...</span>
                </div>
              ) : (
                <>
                  {/* ====================================
                     A. HERO BANNER SLIDES EDIT
                     ==================================== */}
                  {settingsSubTab === 'hero' && (
                    <div className="settings-pane-inner">
                      <div className="pane-header-box">
                        <h2 className="pane-title">Hero Banner Slider Settings</h2>
                        <p className="pane-subtitle">Update text content, percentages, and images displayed in the top homepage slider.</p>
                      </div>

                      <div className="slides-edit-list">
                        {heroSlides.map((slide, index) => (
                          <div key={slide.id || index} className="slide-edit-card">
                            <div className="slide-card-header">
                              <span>Banner Slide #{index + 1}</span>
                              <span className="slide-badge">{slide.offer || '0%'} OFF</span>
                            </div>
                            
                            <div className="slide-form-grid">
                              <div className="form-group">
                                <label className="form-label">Subtitle / Tagline</label>
                                <input 
                                  type="text" 
                                  className="form-input" 
                                  value={slide.subtitle}
                                  onChange={(e) => handleSlideChange(index, 'subtitle', e.target.value)}
                                  placeholder="e.g. DIRECT FROM THE SOURCE"
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Title / Heading</label>
                                <input 
                                  type="text" 
                                  className="form-input" 
                                  value={slide.title}
                                  onChange={(e) => handleSlideChange(index, 'title', e.target.value)}
                                  placeholder="e.g. Pure Himalayan Makhana"
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Offer percentage text</label>
                                <input 
                                  type="text" 
                                  className="form-input" 
                                  value={slide.offer}
                                  onChange={(e) => handleSlideChange(index, 'offer', e.target.value)}
                                  placeholder="e.g. 15%"
                                />
                              </div>

                              <div className="form-group full-width">
                                <label className="form-label">Description text</label>
                                <textarea 
                                  className="form-textarea" 
                                  value={slide.description}
                                  onChange={(e) => handleSlideChange(index, 'description', e.target.value)}
                                  placeholder="Provide a healthy, descriptive snippet..."
                                  rows={3}
                                />
                              </div>

                              {/* Product Image Row */}
                              <div className="form-group image-upload-group full-width">
                                <label className="form-label">Product Image (Foreground png / jpg)</label>
                                <div className="image-edit-row">
                                  <div className="image-preview-wrapper">
                                    {slide.productImage ? (
                                      <img src={resolveImageUrl(slide.productImage)} alt="Product Preview" className="image-edit-preview" />
                                    ) : (
                                      <div className="no-image-preview">No Image</div>
                                    )}
                                  </div>
                                  <div className="upload-controls">
                                    <input 
                                      type="text" 
                                      className="form-input" 
                                      value={slide.productImage}
                                      onChange={(e) => handleSlideChange(index, 'productImage', e.target.value)}
                                      placeholder="Paste product image URL..."
                                    />
                                    <label className="file-upload-btn-label">
                                      <Upload size={14} />
                                      <span>{uploadingField === `slide-${index}-prod` ? 'Uploading...' : 'Upload Photo'}</span>
                                      <input 
                                        type="file" 
                                        accept="image/*" 
                                        style={{ display: 'none' }}
                                        disabled={uploadingField !== null}
                                        onChange={(e) => {
                                          if (e.target.files && e.target.files[0]) {
                                            handleFileUpload(e.target.files[0], `slide-${index}-prod`, (url) => handleSlideChange(index, 'productImage', url));
                                          }
                                        }}
                                      />
                                    </label>
                                  </div>
                                </div>
                              </div>

                              {/* Background Image Row */}
                              <div className="form-group image-upload-group full-width">
                                <label className="form-label">Background Image (Blurred landscape backdrop)</label>
                                <div className="image-edit-row">
                                  <div className="image-preview-wrapper bg-wrapper">
                                    {slide.bgImage ? (
                                      <img src={resolveImageUrl(slide.bgImage)} alt="Background Preview" className="image-edit-preview bg-preview" />
                                    ) : (
                                      <div className="no-image-preview">No Image</div>
                                    )}
                                  </div>
                                  <div className="upload-controls">
                                    <input 
                                      type="text" 
                                      className="form-input" 
                                      value={slide.bgImage}
                                      onChange={(e) => handleSlideChange(index, 'bgImage', e.target.value)}
                                      placeholder="Paste background image URL..."
                                    />
                                    <label className="file-upload-btn-label">
                                      <Upload size={14} />
                                      <span>{uploadingField === `slide-${index}-bg` ? 'Uploading...' : 'Upload Photo'}</span>
                                      <input 
                                        type="file" 
                                        accept="image/*" 
                                        style={{ display: 'none' }}
                                        disabled={uploadingField !== null}
                                        onChange={(e) => {
                                          if (e.target.files && e.target.files[0]) {
                                            handleFileUpload(e.target.files[0], `slide-${index}-bg`, (url) => handleSlideChange(index, 'bgImage', url));
                                          }
                                        }}
                                      />
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="settings-action-bar">
                        <button 
                          className="save-settings-btn" 
                          onClick={() => saveSetting('hero_slider', heroSlides)}
                          disabled={savingKey === 'hero_slider'}
                        >
                          <Save size={16} />
                          <span>{savingKey === 'hero_slider' ? 'Saving changes...' : 'Save Hero Slider Banners'}</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ====================================
                     B. BENEFITS / FEATURES SECTION EDIT
                     ==================================== */}
                  {settingsSubTab === 'features' && (
                    <div className="settings-pane-inner">
                      <div className="pane-header-box">
                        <h2 className="pane-title">Benefits / Features Section Settings</h2>
                        <p className="pane-subtitle">Manage storefront highlights, why-choose-us marketing text, benefits cards, and right-side display photo.</p>
                      </div>

                      <div className="settings-card-form">
                        <div className="slide-card-header">
                          <span>General Benefits Settings</span>
                        </div>
                        <div className="slide-form-grid" style={{ padding: '24px' }}>
                          <div className="form-group full-width">
                            <label className="form-label">Section Title</label>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={featuresData.title}
                              onChange={(e) => setFeaturesData(prev => ({ ...prev, title: e.target.value }))}
                              placeholder="e.g. Why Choose Himalayan Nuts?"
                            />
                          </div>

                          <div className="form-group full-width">
                            <label className="form-label">Section Subtitle / Description</label>
                            <textarea 
                              className="form-textarea" 
                              value={featuresData.description}
                              onChange={(e) => setFeaturesData(prev => ({ ...prev, description: e.target.value }))}
                              placeholder="Provide short marketing text..."
                              rows={3}
                            />
                          </div>

                          {/* Image row */}
                          <div className="form-group image-upload-group full-width">
                            <label className="form-label">Features Section Display Image (Right Side)</label>
                            <div className="image-edit-row">
                              <div className="image-preview-wrapper bowl-wrapper">
                                {featuresData.image ? (
                                  <img src={resolveImageUrl(featuresData.image)} alt="Benefits Preview" className="image-edit-preview bowl-preview" />
                                ) : (
                                  <div className="no-image-preview">No Image</div>
                                )}
                              </div>
                              <div className="upload-controls">
                                <input 
                                  type="text" 
                                  className="form-input" 
                                  value={featuresData.image}
                                  onChange={(e) => setFeaturesData(prev => ({ ...prev, image: e.target.value }))}
                                  placeholder="Paste display image URL..."
                                />
                                <label className="file-upload-btn-label">
                                  <Upload size={14} />
                                  <span>{uploadingField === 'features-img' ? 'Uploading...' : 'Upload Photo'}</span>
                                  <input 
                                    type="file" 
                                    accept="image/*" 
                                    style={{ display: 'none' }}
                                    disabled={uploadingField !== null}
                                    onChange={(e) => {
                                      if (e.target.files && e.target.files[0]) {
                                        handleFileUpload(e.target.files[0], 'features-img', (url) => setFeaturesData(prev => ({ ...prev, image: url })));
                                      }
                                    }}
                                  />
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Benefits Cards */}
                      <div className="pane-header-box" style={{ marginTop: '32px' }}>
                        <h3 className="panel-title" style={{ fontSize: '16px' }}>Benefits Sub-cards (5 total)</h3>
                        <p className="pane-subtitle">Edit the detail blocks loaded below the benefits text.</p>
                      </div>

                      <div className="benefits-cards-grid">
                        {featuresData.benefits && featuresData.benefits.map((benefit, index) => (
                          <div key={index} className="benefit-edit-card">
                            <div className="slide-card-header">
                              <span>Benefit Item #{index + 1}</span>
                            </div>
                            <div className="benefit-card-body">
                              <div className="form-group">
                                <label className="form-label">Benefit Title</label>
                                <input 
                                  type="text" 
                                  className="form-input" 
                                  value={benefit.title}
                                  onChange={(e) => handleBenefitChange(index, 'title', e.target.value)}
                                  placeholder="e.g. High in Protein"
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Benefit Description</label>
                                <textarea 
                                  className="form-textarea" 
                                  value={benefit.description}
                                  onChange={(e) => handleBenefitChange(index, 'description', e.target.value)}
                                  placeholder="Short health summary..."
                                  rows={3}
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Icon Representation</label>
                                <select
                                  className="form-input icon-select-dropdown"
                                  value={benefit.icon}
                                  onChange={(e) => handleBenefitChange(index, 'icon', e.target.value)}
                                >
                                  <option value="Zap">⚡ Protein (Zap icon)</option>
                                  <option value="Leaf">🍃 Nutrients (Leaf icon)</option>
                                  <option value="Heart">❤️ Heart Friendly (Heart icon)</option>
                                  <option value="Shield">🛡️ Immunity Booster (Shield icon)</option>
                                  <option value="Smile">😊 Healthy Snacking (Smile icon)</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="settings-action-bar">
                        <button 
                          className="save-settings-btn" 
                          onClick={() => saveSetting('features_section', featuresData)}
                          disabled={savingKey === 'features_section'}
                        >
                          <Save size={16} />
                          <span>{savingKey === 'features_section' ? 'Saving changes...' : 'Save Benefits Section'}</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ====================================
                     C. FOOTER EDIT
                     ==================================== */}
                  {settingsSubTab === 'footer' && (
                    <div className="settings-pane-inner">
                      <div className="pane-header-box">
                        <h2 className="pane-title">Footer Settings</h2>
                        <p className="pane-subtitle">Manage branding logo titles, quick description text, and official social media hyperlinks.</p>
                      </div>

                      <div className="settings-card-form">
                        <div className="slide-card-header">
                          <span>Footer Branding Details</span>
                        </div>
                        <div className="slide-form-grid" style={{ padding: '24px' }}>
                          <div className="form-group">
                            <label className="form-label">Logo / Brand Title</label>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={footerData.logoTitle}
                              onChange={(e) => setFooterData(prev => ({ ...prev, logoTitle: e.target.value }))}
                              placeholder="e.g. Himalayannuts"
                            />
                          </div>

                          <div className="form-group full-width">
                            <label className="form-label">Footer Description Paragraph</label>
                            <textarea 
                              className="form-textarea" 
                              value={footerData.description}
                              onChange={(e) => setFooterData(prev => ({ ...prev, description: e.target.value }))}
                              placeholder="Brief company bio, organic details..."
                              rows={4}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="settings-card-form" style={{ marginTop: '24px' }}>
                        <div className="slide-card-header">
                          <span>Social Media Hyperlinks</span>
                        </div>
                        <div className="slide-form-grid" style={{ padding: '24px' }}>
                          <div className="form-group">
                            <label className="form-label">Instagram URL</label>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={footerData.instagramUrl}
                              onChange={(e) => setFooterData(prev => ({ ...prev, instagramUrl: e.target.value }))}
                              placeholder="#instagram"
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">Facebook URL</label>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={footerData.facebookUrl}
                              onChange={(e) => setFooterData(prev => ({ ...prev, facebookUrl: e.target.value }))}
                              placeholder="#facebook"
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">YouTube URL</label>
                            <input 
                              type="text" 
                              className="form-input" 
                              value={footerData.youtubeUrl}
                              onChange={(e) => setFooterData(prev => ({ ...prev, youtubeUrl: e.target.value }))}
                              placeholder="#youtube"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="settings-action-bar">
                        <button 
                          className="save-settings-btn" 
                          onClick={() => saveSetting('footer', footerData)}
                          disabled={savingKey === 'footer'}
                        >
                          <Save size={16} />
                          <span>{savingKey === 'footer' ? 'Saving changes...' : 'Save Footer Settings'}</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {settingsSubTab === 'bottom_features' && (
                    <div className="settings-pane-form">
                      <div className="settings-pane-header">
                        <h2 className="pane-title">Bottom Customer Service Features</h2>
                        <p className="pane-subtitle">Manage customer reassurance cards displayed at the bottom of the home page.</p>
                      </div>

                      <div className="settings-pane-body">
                        <div className="admin-grid-2col">
                          {bottomFeaturesData.map((item, index) => (
                            <div key={index} className="slide-editor-card" style={{ padding: '16px', border: '1px solid var(--border-color)', borderRadius: '10px', backgroundColor: '#fcfcfc' }}>
                              <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dark-green)', marginBottom: '12px' }}>
                                Card {index + 1} Settings
                              </h3>
                              
                              <div className="form-group" style={{ marginBottom: '10px' }}>
                                <label className="form-label">Icon Name</label>
                                <select
                                  className="form-input"
                                  value={item.icon}
                                  onChange={(e) => {
                                    const updated = [...bottomFeaturesData];
                                    updated[index].icon = e.target.value;
                                    setBottomFeaturesData(updated);
                                  }}
                                >
                                  <option value="Lock">Lock (Secure Payments)</option>
                                  <option value="RotateCcw">RotateCcw (Easy Returns)</option>
                                  <option value="Truck">Truck (Fast Delivery)</option>
                                  <option value="Headphones">Headphones (Customer Support)</option>
                                  <option value="Zap">Zap (Lightning/Energy)</option>
                                  <option value="Leaf">Leaf (Organic/Natural)</option>
                                  <option value="Heart">Heart (Healthy/Caring)</option>
                                  <option value="Shield">Shield (Protected/Safe)</option>
                                  <option value="Smile">Smile (Friendly/Satisfaction)</option>
                                  <option value="Award">Award (Premium/Quality)</option>
                                  <option value="Gift">Gift (Offers/Presents)</option>
                                  <option value="CheckCircle">CheckCircle (Verified/Completed)</option>
                                  <option value="Star">Star (Rating/Excellent)</option>
                                  <option value="ThumbsUp">ThumbsUp (Like/Trusted)</option>
                                </select>
                              </div>

                              <div className="form-group" style={{ marginBottom: '10px' }}>
                                <label className="form-label">Feature Title</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={item.title}
                                  onChange={(e) => {
                                    const updated = [...bottomFeaturesData];
                                    updated[index].title = e.target.value;
                                    setBottomFeaturesData(updated);
                                  }}
                                  placeholder="Enter feature title"
                                  required
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Feature Subtitle</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={item.subtitle}
                                  onChange={(e) => {
                                    const updated = [...bottomFeaturesData];
                                    updated[index].subtitle = e.target.value;
                                    setBottomFeaturesData(updated);
                                  }}
                                  placeholder="Enter feature description"
                                  required
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="settings-action-bar">
                        <button 
                          className="save-settings-btn" 
                          onClick={() => saveSetting('bottom_features', bottomFeaturesData)}
                          disabled={savingKey === 'bottom_features'}
                        >
                          <Save size={16} />
                          <span>{savingKey === 'bottom_features' ? 'Saving changes...' : 'Save Bottom Features'}</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {settingsSubTab === 'categories' && (
                    <div className="settings-pane-form">
                      <div className="settings-pane-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <h2 className="pane-title">Categories Settings</h2>
                          <p className="pane-subtitle">Manage storefront category names, Hindi translations, and category banner images.</p>
                        </div>
                        <button 
                          className="view-all-btn"
                          style={{ backgroundColor: 'var(--primary-green)', color: '#ffffff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                          onClick={() => {
                            const newId = categoriesData.length > 0 ? Math.max(...categoriesData.map(c => c.id)) + 1 : 1;
                            const newCat = {
                              id: newId,
                              name: 'New Category',
                              hindiName: '',
                              image: 'https://images.unsplash.com/photo-1508061253366-f7da158b6d46?auto=format&fit=crop&q=80&w=300'
                            };
                            setCategoriesData([...categoriesData, newCat]);
                          }}
                        >
                          <Plus size={14} style={{ marginRight: '6px' }} />
                          Add Category
                        </button>
                      </div>

                      <div className="settings-pane-body">
                        <div className="admin-grid-2col">
                          {categoriesData.map((category, index) => (
                            <div key={category.id} className="slide-editor-card" style={{ padding: '16px', border: '1px solid var(--border-color)', borderRadius: '10px', backgroundColor: '#fcfcfc' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dark-green)', margin: 0 }}>
                                  Category {index + 1}: {category.name}
                                </h3>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (window.confirm(`Are you sure you want to delete category "${category.name}"?`)) {
                                      setCategoriesData(categoriesData.filter((_, i) => i !== index));
                                    }
                                  }}
                                  style={{ background: 'none', border: 'none', color: '#ff4757', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}
                                  title="Delete Category"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                              
                              <div className="form-group" style={{ marginBottom: '10px' }}>
                                <label className="form-label">Category Name</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={category.name}
                                  onChange={(e) => {
                                    const updated = [...categoriesData];
                                    updated[index].name = e.target.value;
                                    setCategoriesData(updated);
                                  }}
                                  placeholder="e.g. Almonds"
                                  required
                                />
                              </div>

                              <div className="form-group" style={{ marginBottom: '10px' }}>
                                <label className="form-label">Hindi Translation</label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={category.hindiName}
                                  onChange={(e) => {
                                    const updated = [...categoriesData];
                                    updated[index].hindiName = e.target.value;
                                    setCategoriesData(updated);
                                  }}
                                  placeholder="e.g. Badam"
                                  required
                                />
                              </div>

                              <div className="form-group">
                                <label className="form-label">Category Image URL</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <input
                                    type="text"
                                    className="form-input"
                                    value={category.image}
                                    onChange={(e) => {
                                      const updated = [...categoriesData];
                                      updated[index].image = e.target.value;
                                      setCategoriesData(updated);
                                    }}
                                    placeholder="Enter image URL"
                                    required
                                  />
                                </div>
                                <div style={{ marginTop: '6px' }}>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      if (e.target.files && e.target.files[0]) {
                                        handleFileUpload(e.target.files[0], `cat_img_${index}`, (url) => {
                                          const updated = [...categoriesData];
                                          updated[index].image = url;
                                          setCategoriesData(updated);
                                        });
                                      }
                                    }}
                                    disabled={uploadingField === `cat_img_${index}`}
                                  />
                                  {uploadingField === `cat_img_${index}` && (
                                    <span style={{ fontSize: '11px', color: 'var(--primary-green)', marginLeft: '6px' }}>Uploading...</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="settings-action-bar">
                        <button 
                          className="save-settings-btn" 
                          onClick={() => saveSetting('categories', categoriesData)}
                          disabled={savingKey === 'categories'}
                        >
                          <Save size={16} />
                          <span>{savingKey === 'categories' ? 'Saving changes...' : 'Save Categories Settings'}</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {settingsSubTab === 'payment' && (
                    <div className="settings-pane-form">
                      <div className="settings-pane-header">
                        <h2 className="pane-title">Payment Settings</h2>
                        <p className="pane-subtitle">Configure your store's UPI ID and upload scanner images to automatically request payments from checkout.</p>
                      </div>

                      <div className="settings-pane-body">
                        <div className="form-group" style={{ marginBottom: '20px' }}>
                          <label className="form-label">Merchant UPI ID *</label>
                          <input 
                            type="text" 
                            className="form-input"
                            value={paymentSettingsData.upiId}
                            onChange={(e) => setPaymentSettingsData(prev => ({ ...prev, upiId: e.target.value }))}
                            placeholder="e.g. merchant@upi"
                            required
                          />
                          <p style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '4px' }}>
                            This UPI ID will be used to generate dynamic QR codes that automatically request the exact cart total amount from customers.
                          </p>
                        </div>

                        <div className="form-group">
                          <label className="form-label">Static QR Code Scanner Image (Optional)</label>
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <input 
                              type="text" 
                              className="form-input"
                              value={paymentSettingsData.qrCode}
                              onChange={(e) => setPaymentSettingsData(prev => ({ ...prev, qrCode: e.target.value }))}
                              placeholder="Upload scanner or enter image URL"
                              style={{ flex: 1 }}
                            />
                          </div>
                          <div>
                            <input 
                              type="file" 
                              accept="image/*"
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handleFileUpload(e.target.files[0], 'payment_qr', (url) => {
                                    setPaymentSettingsData(prev => ({ ...prev, qrCode: url }));
                                  });
                                }
                              }}
                              disabled={uploadingField === 'payment_qr'}
                            />
                            {uploadingField === 'payment_qr' && (
                              <span style={{ fontSize: '11px', color: 'var(--primary-green)', marginLeft: '6px' }}>Uploading...</span>
                            )}
                          </div>
                          {paymentSettingsData.qrCode && (
                            <div style={{ marginTop: '16px' }}>
                              <span style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Uploaded Scanner Preview:</span>
                              <img 
                                src={resolveImageUrl(paymentSettingsData.qrCode)} 
                                alt="Scanner Preview" 
                                style={{ maxWidth: '160px', height: 'auto', border: '1px solid #ddd', borderRadius: '8px', padding: '4px' }} 
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="settings-action-bar">
                        <button 
                          className="save-settings-btn" 
                          onClick={() => saveSetting('payment_settings', paymentSettingsData)}
                          disabled={savingKey === 'payment_settings'}
                        >
                          <Save size={16} />
                          <span>{savingKey === 'payment_settings' ? 'Saving changes...' : 'Save Payment Settings'}</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {settingsSubTab === 'branding' && (
                    <div className="settings-pane-form">
                      <div className="settings-pane-header">
                        <h2 className="pane-title">Branding Settings</h2>
                        <p className="pane-subtitle">Customize the storefront brand name, subtitle/tagline, and the logo image.</p>
                      </div>

                      <div className="settings-pane-body">
                        <div className="form-group" style={{ marginBottom: '20px' }}>
                          <label className="form-label">Brand Name *</label>
                          <input 
                            type="text" 
                            className="form-input"
                            value={brandingData.brandName}
                            onChange={(e) => setBrandingData(prev => ({ ...prev, brandName: e.target.value }))}
                            placeholder="e.g. Himalayannuts"
                            required
                          />
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                          <label className="form-label">Tagline / Subtitle *</label>
                          <input 
                            type="text" 
                            className="form-input"
                            value={brandingData.tagline}
                            onChange={(e) => setBrandingData(prev => ({ ...prev, tagline: e.target.value }))}
                            placeholder="e.g. Pure. Natural. Wholesome."
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label className="form-label">Logo Image URL</label>
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <input 
                              type="text" 
                              className="form-input"
                              value={brandingData.logoUrl}
                              onChange={(e) => setBrandingData(prev => ({ ...prev, logoUrl: e.target.value }))}
                              placeholder="Upload logo or enter image URL"
                              style={{ flex: 1 }}
                            />
                          </div>
                          <div>
                            <input 
                              type="file" 
                              accept="image/*"
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handleFileUpload(e.target.files[0], 'branding_logo', (url) => {
                                    setBrandingData(prev => ({ ...prev, logoUrl: url }));
                                  });
                                }
                              }}
                              disabled={uploadingField === 'branding_logo'}
                            />
                            {uploadingField === 'branding_logo' && (
                              <span style={{ fontSize: '11px', color: 'var(--primary-green)', marginLeft: '6px' }}>Uploading...</span>
                            )}
                          </div>
                          {brandingData.logoUrl && (
                            <div style={{ marginTop: '16px' }}>
                              <span style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Logo Preview:</span>
                              <img 
                                src={resolveImageUrl(brandingData.logoUrl)} 
                                alt="Logo Preview" 
                                style={{ maxWidth: '120px', height: 'auto', border: '1px solid #ddd', borderRadius: '8px', padding: '4px' }} 
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="settings-action-bar">
                        <button 
                          className="save-settings-btn" 
                          onClick={() => saveSetting('branding', brandingData)}
                          disabled={savingKey === 'branding'}
                        >
                          <Save size={16} />
                          <span>{savingKey === 'branding' ? 'Saving changes...' : 'Save Branding Settings'}</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {settingsSubTab === 'about_us' && (
                    <div className="settings-pane-form">
                      <div className="settings-pane-header">
                        <h2 className="pane-title">About Us Settings</h2>
                        <p className="pane-subtitle">Customize the content, story details, and brand values displayed on the About Us page.</p>
                      </div>

                      <div className="settings-pane-body">
                        <div className="form-group" style={{ marginBottom: '20px' }}>
                          <label className="form-label">Page Title *</label>
                          <input 
                            type="text" 
                            className="form-input"
                            value={aboutUsData.title}
                            onChange={(e) => setAboutUsData(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="e.g. About Himalayan Nuts"
                            required
                          />
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                          <label className="form-label">Subtitle / Motto *</label>
                          <input 
                            type="text" 
                            className="form-input"
                            value={aboutUsData.subtitle}
                            onChange={(e) => setAboutUsData(prev => ({ ...prev, subtitle: e.target.value }))}
                            placeholder="e.g. Our Story & Vision"
                            required
                          />
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                          <label className="form-label">Story / Description *</label>
                          <textarea 
                            className="form-input"
                            rows="6"
                            value={aboutUsData.story}
                            onChange={(e) => setAboutUsData(prev => ({ ...prev, story: e.target.value }))}
                            placeholder="Write your company story..."
                            style={{ resize: 'vertical', minHeight: '120px' }}
                            required
                          />
                        </div>

                        <div className="form-group" style={{ marginBottom: '24px' }}>
                          <label className="form-label">Story Banner Image URL</label>
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <input 
                              type="text" 
                              className="form-input"
                              value={aboutUsData.image}
                              onChange={(e) => setAboutUsData(prev => ({ ...prev, image: e.target.value }))}
                              placeholder="Upload image or enter image URL"
                              style={{ flex: 1 }}
                            />
                          </div>
                          <div>
                            <input 
                              type="file" 
                              accept="image/*"
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handleFileUpload(e.target.files[0], 'about_image', (url) => {
                                    setAboutUsData(prev => ({ ...prev, image: url }));
                                  });
                                }
                              }}
                              disabled={uploadingField === 'about_image'}
                            />
                            {uploadingField === 'about_image' && (
                              <span style={{ fontSize: '11px', color: 'var(--primary-green)', marginLeft: '6px' }}>Uploading...</span>
                            )}
                          </div>
                          {aboutUsData.image && (
                            <div style={{ marginTop: '16px' }}>
                              <span style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Banner Preview:</span>
                              <img 
                                src={resolveImageUrl(aboutUsData.image)} 
                                alt="Banner Preview" 
                                style={{ maxWidth: '240px', height: 'auto', border: '1px solid #ddd', borderRadius: '8px', padding: '4px' }} 
                              />
                            </div>
                          )}
                        </div>

                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--dark-green)', borderBottom: '1px solid #eee', paddingBottom: '8px', marginBottom: '16px' }}>
                          Brand Core Values
                        </h3>

                        {aboutUsData.values && aboutUsData.values.map((val, index) => (
                          <div key={index} className="slide-editor-card" style={{ padding: '16px', border: '1px solid var(--border-color)', borderRadius: '10px', backgroundColor: '#fcfcfc', marginBottom: '16px' }}>
                            <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--primary-green)', marginTop: 0, marginBottom: '12px' }}>
                              Value #{index + 1}
                            </h4>
                            <div className="form-group" style={{ marginBottom: '10px' }}>
                              <label className="form-label">Value Title</label>
                              <input 
                                type="text"
                                className="form-input"
                                value={val.title}
                                onChange={(e) => {
                                  const updated = [...aboutUsData.values];
                                  updated[index].title = e.target.value;
                                  setAboutUsData(prev => ({ ...prev, values: updated }));
                                }}
                                placeholder="e.g. Pure Quality"
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label className="form-label">Value Description</label>
                              <input 
                                type="text"
                                className="form-input"
                                value={val.description}
                                onChange={(e) => {
                                  const updated = [...aboutUsData.values];
                                  updated[index].description = e.target.value;
                                  setAboutUsData(prev => ({ ...prev, values: updated }));
                                }}
                                placeholder="e.g. Sourced from the finest harvests."
                                required
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="settings-action-bar">
                        <button 
                          className="save-settings-btn" 
                          onClick={() => saveSetting('about_us', aboutUsData)}
                          disabled={savingKey === 'about_us'}
                        >
                          <Save size={16} />
                          <span>{savingKey === 'about_us' ? 'Saving changes...' : 'Save About Us Settings'}</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {settingsSubTab === 'blog_posts' && (
                    <div className="settings-pane-form">
                      <div className="settings-pane-header">
                        <h2 className="pane-title">Blog Settings</h2>
                        <p className="pane-subtitle">Manage, create, and edit healthy living articles published on your storefront blog page.</p>
                      </div>

                      <div className="settings-pane-body">
                        {/* Editor Form */}
                        {isAddingBlogPost || editingBlogPost ? (
                          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
                            <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--dark-green)', marginTop: 0, marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>{isAddingBlogPost ? 'Create Blog Post' : 'Edit Blog Post'}</span>
                              <button 
                                type="button"
                                onClick={() => {
                                  setIsAddingBlogPost(false);
                                  setEditingBlogPost(null);
                                }}
                                style={{ border: 'none', background: 'none', color: '#ff4757', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}
                              >
                                Cancel
                              </button>
                            </h3>

                            <div className="form-group" style={{ marginBottom: '16px' }}>
                              <label className="form-label">Article Title *</label>
                              <input 
                                type="text"
                                className="form-input"
                                value={blogPostForm.title}
                                onChange={(e) => setBlogPostForm(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="e.g. Healthy Living with Himalayan Nuts"
                                required
                              />
                            </div>

                            <div className="form-group" style={{ marginBottom: '16px' }}>
                              <label className="form-label">Author Name *</label>
                              <input 
                                type="text"
                                className="form-input"
                                value={blogPostForm.author}
                                onChange={(e) => setBlogPostForm(prev => ({ ...prev, author: e.target.value }))}
                                placeholder="e.g. Dr. Aarav Mehta"
                                required
                              />
                            </div>

                            <div className="form-group" style={{ marginBottom: '16px' }}>
                              <label className="form-label">Publish Date (e.g. July 10, 2026) *</label>
                              <input 
                                type="text"
                                className="form-input"
                                value={blogPostForm.date}
                                onChange={(e) => setBlogPostForm(prev => ({ ...prev, date: e.target.value }))}
                                placeholder="e.g. July 10, 2026"
                                required
                              />
                            </div>

                            <div className="form-group" style={{ marginBottom: '16px' }}>
                              <label className="form-label">Short Excerpt *</label>
                              <input 
                                type="text"
                                className="form-input"
                                value={blogPostForm.excerpt}
                                onChange={(e) => setBlogPostForm(prev => ({ ...prev, excerpt: e.target.value }))}
                                placeholder="A brief one-sentence summary..."
                                required
                              />
                            </div>

                            <div className="form-group" style={{ marginBottom: '20px' }}>
                              <label className="form-label">Full Article Content *</label>
                              <textarea 
                                className="form-input"
                                rows="8"
                                value={blogPostForm.content}
                                onChange={(e) => setBlogPostForm(prev => ({ ...prev, content: e.target.value }))}
                                placeholder="Write the full post contents..."
                                style={{ resize: 'vertical', minHeight: '160px' }}
                                required
                              />
                            </div>

                            <div className="form-group" style={{ marginBottom: '24px' }}>
                              <label className="form-label">Banner Image URL</label>
                              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <input 
                                  type="text" 
                                  className="form-input"
                                  value={blogPostForm.image}
                                  onChange={(e) => setBlogPostForm(prev => ({ ...prev, image: e.target.value }))}
                                  placeholder="Upload image or enter image URL"
                                  style={{ flex: 1 }}
                                />
                              </div>
                              <div>
                                <input 
                                  type="file" 
                                  accept="image/*"
                                  onChange={(e) => {
                                    if (e.target.files && e.target.files[0]) {
                                      handleFileUpload(e.target.files[0], 'blog_image', (url) => {
                                        setBlogPostForm(prev => ({ ...prev, image: url }));
                                      });
                                    }
                                  }}
                                  disabled={uploadingField === 'blog_image'}
                                />
                                {uploadingField === 'blog_image' && (
                                  <span style={{ fontSize: '11px', color: 'var(--primary-green)', marginLeft: '6px' }}>Uploading...</span>
                                )}
                              </div>
                              {blogPostForm.image && (
                                <div style={{ marginTop: '16px' }}>
                                  <span style={{ fontSize: '11px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Banner Preview:</span>
                                  <img 
                                    src={resolveImageUrl(blogPostForm.image)} 
                                    alt="Banner Preview" 
                                    style={{ maxWidth: '240px', height: 'auto', border: '1px solid #ddd', borderRadius: '8px', padding: '4px' }} 
                                  />
                                </div>
                              )}
                            </div>

                            <button 
                              type="button"
                              className="save-settings-btn" 
                              onClick={() => {
                                if (!blogPostForm.title || !blogPostForm.author || !blogPostForm.content || !blogPostForm.excerpt) {
                                  alert('Please fill out all required fields.');
                                  return;
                                }
                                let updatedPosts = [...blogPostsData];
                                if (isAddingBlogPost) {
                                  const newId = updatedPosts.length > 0 ? Math.max(...updatedPosts.map(p => p.id)) + 1 : 1;
                                  updatedPosts.push({
                                    id: newId,
                                    ...blogPostForm
                                  });
                                } else {
                                  updatedPosts = updatedPosts.map(post => 
                                    post.id === editingBlogPost.id ? { ...post, ...blogPostForm } : post
                                  );
                                }
                                saveSetting('blog_posts', updatedPosts);
                              }}
                              disabled={savingKey === 'blog_posts'}
                            >
                              <Save size={16} />
                              <span>{savingKey === 'blog_posts' ? 'Saving article...' : 'Save Blog Article'}</span>
                            </button>
                          </div>
                        ) : (
                          /* List View */
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                              <button 
                                className="add-category-btn"
                                onClick={() => {
                                  setIsAddingBlogPost(true);
                                  setEditingBlogPost(null);
                                  setBlogPostForm({
                                    title: '',
                                    excerpt: '',
                                    content: '',
                                    image: 'https://images.unsplash.com/photo-1543362906-acfc16c67564?auto=format&fit=crop&q=80&w=600',
                                    date: new Date().toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' }),
                                    author: 'Admin'
                                  });
                                }}
                              >
                                <Plus size={14} />
                                <span>Create Blog Post</span>
                              </button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                              {blogPostsData.length === 0 ? (
                                <p style={{ fontSize: '14px', color: 'var(--gray)', textAlign: 'center', padding: '40px 0' }}>
                                  No blog posts published yet. Click the button above to publish your first post!
                                </p>
                              ) : (
                                blogPostsData.map(post => (
                                  <div key={post.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', border: '1px solid var(--border-color)', borderRadius: '12px', backgroundColor: '#fff' }}>
                                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                      {post.image && (
                                        <img 
                                          src={resolveImageUrl(post.image)} 
                                          alt={post.title} 
                                          style={{ width: '80px', height: '60px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #eee' }} 
                                        />
                                      )}
                                      <div>
                                        <h4 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--dark-green)', margin: '0 0 4px 0' }}>
                                          {post.title}
                                        </h4>
                                        <p style={{ fontSize: '11px', color: 'var(--gray)', margin: 0 }}>
                                          Published on {post.date} by {post.author}
                                        </p>
                                      </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                      <button 
                                        onClick={() => {
                                          setEditingBlogPost(post);
                                          setBlogPostForm({
                                            title: post.title,
                                            excerpt: post.excerpt,
                                            content: post.content,
                                            image: post.image || '',
                                            date: post.date,
                                            author: post.author
                                          });
                                          setIsAddingBlogPost(false);
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', color: 'var(--primary-green)' }}
                                      >
                                        <Edit size={14} />
                                      </button>
                                      <button 
                                        onClick={() => {
                                          if (window.confirm('Are you sure you want to delete this blog post?')) {
                                            const updated = blogPostsData.filter(p => p.id !== post.id);
                                            saveSetting('blog_posts', updated);
                                          }
                                        }}
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', color: '#e03131' }}
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {settingsSubTab === 'contact_settings' && (
                    <div className="settings-pane-form">
                      <div className="settings-pane-header">
                        <h2 className="pane-title">Contact Settings</h2>
                        <p className="pane-subtitle">Configure contact information and social media links displayed on the Contact Us page.</p>
                      </div>

                      <div className="settings-pane-body">
                        <div className="form-group" style={{ marginBottom: '20px' }}>
                          <label className="form-label">Contact Email Address *</label>
                          <input 
                            type="email" 
                            className="form-input"
                            value={contactSettingsData.email}
                            onChange={(e) => setContactSettingsData(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="e.g. support@himalayannuts.com"
                            required
                          />
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                          <label className="form-label">Contact Phone Number *</label>
                          <input 
                            type="text" 
                            className="form-input"
                            value={contactSettingsData.phone}
                            onChange={(e) => setContactSettingsData(prev => ({ ...prev, phone: e.target.value }))}
                            placeholder="e.g. +91 98765 43210"
                            required
                          />
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                          <label className="form-label">Store Address *</label>
                          <input 
                            type="text" 
                            className="form-input"
                            value={contactSettingsData.address}
                            onChange={(e) => setContactSettingsData(prev => ({ ...prev, address: e.target.value }))}
                            placeholder="e.g. Pristine Valleys, HP, India"
                            required
                          />
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                          <label className="form-label">WhatsApp Link / Number API URL *</label>
                          <input 
                            type="text" 
                            className="form-input"
                            value={contactSettingsData.whatsapp}
                            onChange={(e) => setContactSettingsData(prev => ({ ...prev, whatsapp: e.target.value }))}
                            placeholder="e.g. https://wa.me/919876543210"
                            required
                          />
                          <p style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '4px' }}>
                            Format: Use <strong>https://wa.me/yournumber</strong> (including country code, without + or spaces).
                          </p>
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                          <label className="form-label">Instagram Link *</label>
                          <input 
                            type="text" 
                            className="form-input"
                            value={contactSettingsData.instagram}
                            onChange={(e) => setContactSettingsData(prev => ({ ...prev, instagram: e.target.value }))}
                            placeholder="e.g. https://instagram.com/himalayannuts"
                            required
                          />
                        </div>

                        <div className="form-group" style={{ marginBottom: '20px' }}>
                          <label className="form-label">Facebook Link *</label>
                          <input 
                            type="text" 
                            className="form-input"
                            value={contactSettingsData.facebook}
                            onChange={(e) => setContactSettingsData(prev => ({ ...prev, facebook: e.target.value }))}
                            placeholder="e.g. https://facebook.com/himalayannuts"
                            required
                          />
                        </div>
                      </div>

                      <div className="settings-action-bar">
                        <button 
                          className="save-settings-btn" 
                          onClick={() => saveSetting('contact_settings', contactSettingsData)}
                          disabled={savingKey === 'contact_settings'}
                        >
                          <Save size={16} />
                          <span>{savingKey === 'contact_settings' ? 'Saving changes...' : 'Save Contact Settings'}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        </main>
      )}

      {activeMainTab === 'products' && (
        /* ====================================
           3. PRODUCTS VIEW
           ==================================== */
        <main className="container admin-main-content">
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <h1 className="login-card-title" style={{ margin: 0, fontSize: '24px', textAlign: 'left' }}>Product Catalog</h1>
              <p className="login-card-subtitle" style={{ margin: '4px 0 0 0', textAlign: 'left' }}>
                Manage storefront products, prices, sections, badges, and review metrics.
              </p>
            </div>
            {!isAddingProduct && !editingProduct && (
              <button 
                className="btn btn-primary btn-save" 
                onClick={() => {
                  setEditingProduct(null);
                  setProductForm({
                    title: '',
                    price: '',
                    oldPrice: '',
                    image: '',
                    badge: '',
                    rating: '5.0',
                    reviewsCount: '0',
                    type: 'nuts'
                  });
                  setIsAddingProduct(true);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '8px', backgroundColor: 'var(--primary-green)', color: '#ffffff', border: 'none', cursor: 'pointer', fontWeight: 600 }}
              >
                <Plus size={16} />
                <span>Add Product</span>
              </button>
            )}
          </div>

          {/* Form: Add or Edit Product */}
          {(isAddingProduct || editingProduct) && (
            <div className="settings-pane-form" style={{ backgroundColor: '#ffffff', padding: '24px', borderRadius: '12px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--dark-green)', marginBottom: '20px' }}>
                {editingProduct ? `Edit Product: ${editingProduct.title}` : 'Add New Product'}
              </h2>

              <form onSubmit={handleProductSubmit}>
                <div className="admin-grid-2col" style={{ marginBottom: '20px' }}>
                  <div className="form-group">
                    <label className="form-label">Product Title *</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      value={productForm.title} 
                      onChange={(e) => setProductForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g. California Almonds (Premium)"
                      required 
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Category / Type *</label>
                    <select 
                      className="form-input" 
                      value={productForm.type} 
                      onChange={(e) => setProductForm(prev => ({ ...prev, type: e.target.value }))}
                      required
                    >
                      <option value="">Select Category</option>
                      {categoriesData.map((cat) => (
                        <option key={cat.id} value={cat.name.toLowerCase()}>
                          {cat.name} {cat.hindiName ? `(${cat.hindiName})` : ''}
                        </option>
                      ))}
                      {/* Fallback for pre-existing types not matching custom list */}
                      {productForm.type && !categoriesData.some(cat => cat.name.toLowerCase() === productForm.type.toLowerCase()) && (
                        <option value={productForm.type}>{productForm.type}</option>
                      )}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Price (₹) *</label>
                    <input 
                      type="number" 
                      step="0.01"
                      className="form-input" 
                      value={productForm.price} 
                      onChange={(e) => setProductForm(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="e.g. 499"
                      required 
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Old Price (₹) - Strike through price</label>
                    <input 
                      type="number" 
                      step="0.01"
                      className="form-input" 
                      value={productForm.oldPrice} 
                      onChange={(e) => setProductForm(prev => ({ ...prev, oldPrice: e.target.value }))}
                      placeholder="e.g. 599"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Product Badge (e.g. "Best Seller", "15% OFF")</label>
                    <input 
                      type="text" 
                      className="form-input" 
                      value={productForm.badge} 
                      onChange={(e) => setProductForm(prev => ({ ...prev, badge: e.target.value }))}
                      placeholder="Leave blank for no badge"
                    />
                  </div>

                  <div className="form-group">
                    <div className="admin-grid-2col-small">
                      <div>
                        <label className="form-label">Review Rating *</label>
                        <input 
                          type="number" 
                          step="0.1"
                          min="1"
                          max="5"
                          className="form-input" 
                          value={productForm.rating} 
                          onChange={(e) => setProductForm(prev => ({ ...prev, rating: e.target.value }))}
                          placeholder="e.g. 4.9"
                          required 
                        />
                      </div>
                      <div>
                        <label className="form-label">Reviews Count *</label>
                        <input 
                          type="number" 
                          min="0"
                          className="form-input" 
                          value={productForm.reviewsCount} 
                          onChange={(e) => setProductForm(prev => ({ ...prev, reviewsCount: e.target.value }))}
                          placeholder="e.g. 148"
                          required 
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: '24px' }}>
                  <label className="form-label">Product Image URL *</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input 
                      type="text" 
                      className="form-input" 
                      value={productForm.image} 
                      onChange={(e) => setProductForm(prev => ({ ...prev, image: e.target.value }))}
                      placeholder="Enter Image URL or upload below"
                      required 
                    />
                  </div>
                  <div style={{ marginTop: '10px', padding: '12px', border: '1px dashed var(--border-color)', borderRadius: '8px', backgroundColor: '#fcfcfc' }}>
                    <span className="form-label" style={{ display: 'block', marginBottom: '6px' }}>Or upload an image file:</span>
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleFileUpload(e.target.files[0], 'product_image', (url) => {
                            setProductForm(prev => ({ ...prev, image: url }));
                          });
                        }
                      }}
                      disabled={uploadingField === 'product_image'}
                    />
                    {uploadingField === 'product_image' && (
                      <span style={{ fontSize: '12px', color: 'var(--primary-green)', marginLeft: '10px' }}>Uploading to Cloudinary...</span>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button 
                    type="button" 
                    className="admin-logout-btn" 
                    onClick={() => {
                      setEditingProduct(null);
                      setIsAddingProduct(false);
                      setProductForm(initialProductForm);
                    }}
                    style={{ backgroundColor: '#f1f2f6', color: '#2f3542', border: '1px solid var(--border-color)', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-save"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '8px', backgroundColor: 'var(--primary-green)', color: '#ffffff', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                  >
                    <Save size={16} />
                    <span>Save Product</span>
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Table list: Product Catalog */}
          {!isAddingProduct && !editingProduct && (
            <div className="orders-table-wrapper table-responsive-container" style={{ backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid var(--border-color)', overflowX: 'auto', boxShadow: 'var(--shadow-sm)' }}>
              {loadingProducts ? (
                <div style={{ padding: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  <RefreshCw size={24} className="spin-animation" style={{ color: 'var(--primary-green)' }} />
                  <span style={{ color: 'var(--gray)', fontWeight: 500 }}>Loading product catalog...</span>
                </div>
              ) : products.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--gray)' }}>
                  No products found in the database. Use the "Add Product" button above or seed initial products.
                </div>
              ) : (
                <table className="admin-orders-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid var(--border-color)' }}>
                      <th style={{ padding: '16px 20px', fontWeight: 600, color: 'var(--dark-green)' }}>Image</th>
                      <th style={{ padding: '16px 20px', fontWeight: 600, color: 'var(--dark-green)' }}>Product Details</th>
                      <th style={{ padding: '16px 20px', fontWeight: 600, color: 'var(--dark-green)' }}>Category</th>
                      <th style={{ padding: '16px 20px', fontWeight: 600, color: 'var(--dark-green)' }}>Badge</th>
                      <th style={{ padding: '16px 20px', fontWeight: 600, color: 'var(--dark-green)' }}>Rating & Reviews</th>
                      <th style={{ padding: '16px 20px', fontWeight: 600, color: 'var(--dark-green)' }}>Price</th>
                      <th style={{ padding: '16px 20px', fontWeight: 600, color: 'var(--dark-green)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id} style={{ borderBottom: '1px solid var(--border-color)', verticalAlign: 'middle' }}>
                        <td style={{ padding: '12px 20px' }}>
                          <img 
                            src={resolveImageUrl(product.image)} 
                            alt={product.title} 
                            style={{ width: '48px', height: '48px', objectFit: 'contain', borderRadius: '6px', backgroundColor: '#f9f9f9', border: '1px solid #eee' }} 
                          />
                        </td>
                        <td style={{ padding: '12px 20px' }}>
                          <div style={{ fontWeight: 600, color: 'var(--dark-text)' }}>{product.title}</div>
                          <div style={{ fontSize: '11px', color: 'var(--gray)', marginTop: '2px' }}>ID: {product.id}</div>
                        </td>
                        <td style={{ padding: '12px 20px', textTransform: 'capitalize', fontWeight: 500 }}>
                          <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: product.type === 'makhana' ? '#eef2f7' : product.type === 'nuts' ? '#fff5f5' : '#f0fbf0', color: product.type === 'makhana' ? '#1e375a' : product.type === 'nuts' ? '#c92a2a' : '#2b8a3e', fontSize: '12px' }}>
                            {product.type}
                          </span>
                        </td>
                        <td style={{ padding: '12px 20px' }}>
                          {product.badge ? (
                            <span style={{ padding: '2px 6px', fontSize: '11px', fontWeight: 600, borderRadius: '4px', backgroundColor: '#ffe8cc', color: '#d9480f' }}>
                              {product.badge}
                            </span>
                          ) : (
                            <span style={{ color: '#ccc' }}>-</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 20px', fontSize: '13px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}>
                            <span style={{ color: '#f59e0b' }}>★</span> {product.rating.toFixed(1)}
                          </div>
                          <div style={{ color: 'var(--gray)', fontSize: '11px', marginTop: '2px' }}>{product.reviewsCount} verified reviews</div>
                        </td>
                        <td style={{ padding: '12px 20px' }}>
                          <div style={{ fontWeight: 700, color: 'var(--dark-green)' }}>₹{product.price}</div>
                          {product.oldPrice > 0 && (
                            <div style={{ fontSize: '12px', color: '#a0a0a0', textDecoration: 'line-through' }}>₹{product.oldPrice}</div>
                          )}
                        </td>
                        <td style={{ padding: '12px 20px' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button 
                              onClick={() => {
                                setEditingProduct(product);
                                setProductForm({
                                  title: product.title,
                                  price: product.price.toString(),
                                  oldPrice: product.oldPrice ? product.oldPrice.toString() : '',
                                  image: product.image,
                                  badge: product.badge || '',
                                  rating: product.rating.toString(),
                                  reviewsCount: product.reviewsCount.toString(),
                                  type: product.type
                                });
                                setIsAddingProduct(false);
                              }}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', color: 'var(--primary-green)' }}
                              title="Edit Product"
                            >
                              <Edit size={14} />
                            </button>
                            <button 
                              onClick={() => handleProductDelete(product.id)}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', color: '#e03131' }}
                              title="Delete Product"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </main>
      )}

      {selectedScreenshot && (
        <div className="lightbox-modal-overlay">
          <div className="lightbox-content-box">
            <button onClick={() => setSelectedScreenshot(null)} className="lightbox-close-btn">
              <X size={16} />
            </button>
            <h3 style={{ fontFamily: 'var(--font-nav)', fontWeight: 600, fontSize: '16px', color: 'var(--dark-green)' }}>
              Uploaded UPI Payment Screenshot
            </h3>
            <img src={selectedScreenshot} alt="UPI Payment Receipt Screenshot" className="lightbox-img" />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
